import {getDoc, getDocs} from "firebase/firestore";
import {postApi} from "@/api";
import {PersistentModel} from "@/models/PersistentModel";
import { CommentType, normalizeCommentType } from "@strenforcement/domain/comment";

function getStandaloneCommentPath(parent) {
  const cityId = parent?.city?.id;
  const parentId = parent?.id;
  const parentCollection = parent?.constructor?.collectionName;

  if (!cityId || !parentId || !parentCollection) {
    throw new Error('Could not determine comment endpoint for model');
  }

  return `/v1/cities/${cityId}/${parentCollection}/${parentId}/comments`;
}

export default class ListingComment extends PersistentModel {

  static collectionName = 'comments';

  /**
   *
   * @param {PersistentModel} parent
   * @return {Promise<ListingComment[]>}
   */
  static async list(/* PersistentModel */ parent) {
    const snapshot = await getDocs(parent.subCollection('comments'));
    return snapshot.docs.map(doc => {
      return new ListingComment(parent, doc);
    });
  }

  /**
   *
   * @param {PersistentModel} parent
   * @param {string} message
   * @return {Promise<ListingComment>}
   */
  static async create(/* PersistentModel */ parent, message) {
    const response = await postApi(getStandaloneCommentPath(parent), { message });
    const commentId = response?.comment?.id;
    if (!commentId) {
      throw new Error('Comment was created but no comment id was returned');
    }

    const docRef = parent.subDoc('comments', commentId);
    const doc = await getDoc(docRef);
    return new ListingComment(parent, doc);
  }

  constructor(parent, doc) {
    super(doc);
    this.parent = parent;
  }

  get message() {
    return this.data.message;
  }

  get type() {
    return normalizeCommentType(this.data.type);
  }

  get metadata() {
    return this.data.metadata || {};
  }

  get isStandalone() {
    return this.type === CommentType.STANDALONE;
  }

  get isVerification() {
    return this.type === CommentType.VERIFICATION;
  }

  get isDeferral() {
    return this.type === CommentType.DEFERRAL;
  }

  get isCorrespondence() {
    return this.type === CommentType.CORRESPONDENCE;
  }

  get isLicenseEdit() {
    return this.type === CommentType.LICENSE_EDIT;
  }

  async updateMessage(message) {
    await this.update({
      message,
      updatedAt: new Date()
    }, 'updateMessage');
  }

  get createdBy() {
    return this.data.createdBy;
  }

  get createdAt() {
    return this.data.createdAt;
  }

  get updatedAt() {
    return this.data.updatedAt;
  }

  get isCreatedByCurrentUser() {
    const currentUser = this.parent.currentUser || {};
    const createdBy = this.createdBy || {};
    return (currentUser.id && currentUser.id === createdBy.id)
      || (currentUser.uid && currentUser.uid === createdBy.uid);
  }
}
